import { useAuth } from "@zkt/eb-utility";
import { fromEvent } from "rxjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  registerApplication,
  unregisterApplication,
  getAppNames,
  SingleSpaCustomEventDetail,
  getAppStatus,
} from "single-spa";
import { useIframeMessage } from "@/hooks/iframe-message";
import "./style.scss";

function formatUrl(url: string, removeAuth = true): string {
  if (!url) return undefined;
  let result = url.replace("?#", "#").replace(/[?&]$/, "");
  if (result.includes("&") && !result.includes("?")) {
    result = result.replace("&", "?");
  }
  if (removeAuth) {
    result = result.replace(/auth=[^&]*/, "");
  }
  return result;
}

interface IframeMicroAppProps {
  name: string;
  onLoading: (state: boolean) => void;
  activeWhen: Array<string> | ((path: Location) => boolean);
  getUrl(): string;
  spa?: boolean;
}

export default function IframeMicroApp(
  props: IframeMicroAppProps
): JSX.Element {
  const { name, getUrl, onLoading, activeWhen, spa } = props;
  const container = useRef<HTMLIFrameElement>(null);
  const { auth } = useAuth();

  const [status, setStatus] = useState("");

  const load = useCallback(
    (from: string) => {
      return new Promise((resolve, reject) => {
        onLoading(true);

        const src = getUrl();
        const iframe = document.createElement("iframe");
        const timer = setTimeout(resolve, 10 * 1000);
        iframe.onload = () => {
          clearTimeout(timer);
          resolve(from);
        };
        iframe.onerror = reject;
        iframe.src = formatUrl(src, false);
        const old = container.current.querySelector("iframe");
        if (old) {
          container.current.removeChild(old);
        }
        container.current.appendChild(iframe);
      }).finally(() => onLoading(false));
    },
    [container, getUrl, onLoading]
  );

  useEffect(() => {
    const subscription = fromEvent(
      window,
      "single-spa:routing-event"
    ).subscribe((ev: Event & { detail: SingleSpaCustomEventDetail }) => {
      const originalEvent = ev?.detail?.originalEvent as PopStateEvent;
      if (originalEvent?.state?.state?.source === "iframe") {
        return ev.preventDefault();
      }
      // 微应用加载之后，框架不再处理路由变化，需要在微应用自己处理内部跳转逻辑
      if (
        ev.detail.newUrl !== ev.detail.oldUrl &&
        status === "mounted" &&
        originalEvent
      ) {
        // 只有在当前微应用内部跳转时才处理
        if (spa && window.postMessage && container.current) {
          const iframe = container.current.querySelector("iframe");
          if (!iframe) return undefined;
          const path = window.location.href
            .replace(origin, "")
            .replace(/auth=[^&]*/, "")
            .replace(/&+/g, "&");
          iframe.contentWindow.postMessage({ type: "route", path }, "*");
          const timeout = setTimeout(() => {
            // eslint-disable-next-line no-console
            console.warn("route timeout");
            load("routing-event");
          }, 1000);
          const resp = fromEvent(window, "message").subscribe(
            (e: MessageEvent) => {
              if (e.data.type === "routeresp" && e.data.path === path) {
                clearTimeout(timeout);
                resp.unsubscribe();
              }
            }
          );
        } else {
          load("routing-event");
        }
      }
      return undefined;
    });
    return () => subscription.unsubscribe();
  }, [status, load, container, spa]);

  useEffect(() => {
    const register = () =>
      registerApplication({
        name,
        activeWhen,
        app: {
          bootstrap() {
            return Promise.resolve();
          },
          mount() {
            return load("register").then(() => setStatus("mounted"));
          },
          unmount() {
            setStatus("not_mounted");
            container.current.innerHTML = "";
            return Promise.resolve();
          },
        },
      });
    const apps = getAppNames();
    if (apps.includes(name)) {
      unregisterApplication(name).then(() => register());
    } else {
      register();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, name]);

  useEffect(() => {
    // 跳过首次执行
    if (getAppStatus(name) !== "MOUNTED") return;
    // 授权信息变更时重新加载页面
    if (!auth?.token || !container.current.querySelector("iframe")) return;
    load("auth");
  }, [name, auth?.token, load]);
  const message = useIframeMessage();
  useEffect(() => {
    if (!message) return;
    const { data } = message;
    // iframe内部页面跳转时，同步更新路由地址
    if (data?.type !== "route" || !container.current) return;
    const iframe = container.current.querySelector("iframe");
    if (!iframe) return;
    if (data.name === "push" && data.value) {
      window.history.pushState({ source: "iframe" }, "", data.value.path);
    } else if (data.name === "replace" && data.value) {
      const { fullPath, path } = data.value;
      let iframeUrl = `${data.source === "ebooking" ? "#" : ""}${fullPath}`;
      if (!iframeUrl.startsWith("/")) {
        iframeUrl = `/${iframeUrl}`;
      }
      const { hash, pathname, href, origin } = window.location;
      if (pathname === "/" && !hash && path === "/dashboard") {
        return;
      }
      if (href.replace(origin, "") === iframeUrl) {
        return;
      }
      iframeUrl = formatUrl(iframeUrl);
      window.history.replaceState({ source: "iframe" }, null, iframeUrl);
    }
  }, [message]);

  return (
    <div
      ref={container}
      className={`iframe ${name} ${status}`}
      id={`single-spa-application:${name}`}
    />
  );
}

IframeMicroApp.prototype.defaultProps = {
  spa: false,
};
